// ══════════════════════════════════════════════════════════════
//  GEOBAJA — Shell: acceso a datos
// ══════════════════════════════════════════════════════════════
// Lo que el shell pide por sí mismo: configuración global (admin_config), perfiles
// (actividad y presencia) y los hechos que alimentan los avisos del sidebar. Los
// catálogos de dominio y los callbacks de escritura que el shell reparte a las vistas
// viven en la capa de datos de cada dominio (obras.data, estimaciones.data,
// presupuestos.data, clientes.data). Contrato: cada función devuelve EXACTAMENTE lo que
// devuelve `sb` (arreglo de filas, o `{ error }`; nunca lanza). Sin lógica de negocio.
import { sb } from '../supabaseClient.js';

// ── Configuración global ──────────────────────────────────────
export function cargarAdminConfig() {
  return sb.from('admin_config').select('*');
}
export function guardarAdminConfig(key, value) {
  return sb.from('admin_config').upsert({ key, value, updated_at: new Date().toISOString() }, 'key');
}

// ── Perfiles: presencia ───────────────────────────────────────
export function cargarPerfilesConActividad() {
  return sb.from('perfiles').select('id,nombre,apellido,rol,ultima_actividad');
}
export function registrarActividadPerfil(perfilId) {
  return sb.from('perfiles').update({ ultima_actividad: new Date().toISOString() }, `id=eq.${perfilId}`);
}

// ── Hechos para los avisos del sidebar (QC y laboratorio) ─────
// El shell arma el filtro PostgREST (obras asignadas, fechas); aquí solo se pide.
export function cargarIdsColadosDeObras(obraIds) {
  return sb.from('colados').select('id', `obra_id=in.(${obraIds.join(',')})`);
}
export function cargarIdsMuestrasDeColados(coladoIds) {
  return sb.from('muestras').select('id', `colado_id=in.(${coladoIds.join(',')})`);
}
export function cargarEspecimenesConFiltro(filtro) {
  return sb.from('especimenes').select('id,fecha_ensaye', filtro);
}
export function cargarReportesCompConFiltro(filtro) {
  return sb.from('comp_reportes').select('id,estado', filtro);
}
export function cargarLabMuestrasConFiltro(filtro) {
  return sb.from('lab_muestras').select('id,muestreado_at,created_at', filtro);
}
export function cargarLabEquiposActivos() {
  return sb.from('lab_equipos').select('id', 'activo=eq.true');
}
export function cargarLabCalibraciones(columnas = 'equipo_id,fecha_calibracion,fecha_vencimiento,created_at') {
  return sb.from('lab_calibraciones').select(columnas);
}

/** Una sola clave de admin_config (fila completa). */
export function cargarAdminConfigClave(key) {
  return sb.from('admin_config').select('*', `key=eq.${key}`);
}

export function cargarPerfiles(columnas = '*', orden = null) {
  return sb.from('perfiles').select(columnas, orden ? `order=${orden}` : undefined);
}

// ── Perfiles: sesión y administración de usuarios ─────────────
/** `opciones.signal` corta la lectura: la entrada y la sesión restaurada le ponen tiempo límite (acceso/perfil.js). */
export function cargarPerfil(perfilId, columnas = '*', opciones) {
  const signal = opciones && opciones.signal;
  return signal
    ? sb.from('perfiles').select(columnas, `id=eq.${perfilId}`, { signal })
    : sb.from('perfiles').select(columnas, `id=eq.${perfilId}`);
}
export function cargarPerfilesActivos(columnas = '*', orden = 'nombre.asc') {
  return sb.from('perfiles').select(columnas, `activo=eq.true&order=${orden}`);
}
/** Rol, obras asignadas, activo y firma de correo: RLS y el trigger protect_perfiles_priv deciden quién puede. */
export function actualizarPerfil(perfilId, cambios) {
  return sb.from('perfiles').update(cambios, `id=eq.${perfilId}`);
}

// ── Configuración global: por clave y por patrón ─────────────
export function cargarAdminConfigValor(key) {
  return sb.from('admin_config').select('value', `key=eq.${key}`);
}
export function cargarAdminConfigPorPatron(patron) {
  return sb.from('admin_config').select('*', `key=like.${patron}`);
}
export function cargarLabAutorizaciones(columnas = '*') {
  return sb.from('lab_autorizaciones').select(columnas);
}

// ── Notificaciones push (suscripción del dispositivo) ────────
export function guardarSuscripcionPush(fila) {
  return sb.from('push_subscriptions').upsert(fila, 'endpoint');
}
export function eliminarSuscripcionPush(endpoint) {
  return sb.from('push_subscriptions').delete(`endpoint=eq.${encodeURIComponent(endpoint)}`);
}

// ── Reporte público (QR): folio para cualquier módulo ─────────
export function registrarReportePublico(args) {
  return sb.rpc('registrar_reporte_publico', args);
}

/** Directorio interno (RPC): nombre, correo y rol de cada persona activa. */
export function cargarDirectorioInterno() {
  return sb.rpc('directorio_interno', {});
}

export function cargarPerfilesPorRol(rol, columnas = '*') {
  return sb.from('perfiles').select(columnas, `rol=eq.${rol}`);
}
